import { useMemo } from 'react'
import type { RoomPreviewChip } from '@/design-system'
import { getWizardColors, getWizardFrames, getWizardMaterials, getWizardScenes } from '../catalog'
import { FRAME_NONE, type WizardDraft } from '../types'

export interface WizardPreviewData {
  sceneThumbUrl?: string
  sceneName?: string
  wallTint?: string
  photoUrl?: string
  matColor: string
  matWidthPct: number
  chips: RoomPreviewChip[]
}

/**
 * Resolves the wizard's in-progress selections (IDs) into the display data
 * a live `RoomPreview` needs — same catalog lookups `ReviewSummary` does,
 * just kept live across every step instead of only at the end.
 */
export function useWizardPreviewData(
  draft: WizardDraft,
  fallbackPhotoUrl?: string,
): WizardPreviewData {
  const scene = useMemo(
    () => getWizardScenes().find((s) => s.id === draft.sceneId),
    [draft.sceneId],
  )
  const frame = useMemo(() => {
    if (draft.frameId == null || draft.frameId === FRAME_NONE) return null
    return getWizardFrames().find((f) => f.id === draft.frameId)
  }, [draft.frameId])
  const material = useMemo(
    () => getWizardMaterials().find((m) => m.id === draft.materialId),
    [draft.materialId],
  )
  const color = useMemo(
    () => getWizardColors().find((c) => c.id === draft.colorId),
    [draft.colorId],
  )

  return useMemo(() => {
    const activeSlot = draft.slots.find((s) => s.photoId && !s.excluded)
    const photoUrl = activeSlot?.thumbUrl || activeSlot?.url || fallbackPhotoUrl

    const chips: RoomPreviewChip[] = []
    if (frame) chips.push({ label: frame.name, swatchUrl: frame.thumbUrl })
    if (material) chips.push({ label: material.name, swatchUrl: material.thumbUrl })
    if (color) chips.push({ label: color.name, swatchColor: color.hex })

    return {
      sceneThumbUrl: scene?.thumbUrl,
      sceneName: scene?.name,
      wallTint: color?.hex,
      photoUrl,
      matColor: draft.matColor,
      matWidthPct: Math.round(draft.matWidth * 100),
      chips,
    }
  }, [scene, frame, material, color, draft.slots, draft.matColor, draft.matWidth, fallbackPhotoUrl])
}
